import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, Observable, of, Subscription, TeardownLogic } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { SessionStorage } from 'ngx-webstorage';

import { ConfigService } from 'services/config/config.service';

import { AdvertisedJobInfo, CodeTableValue, Country, JobDescriptionInfo, SystemConfigInfo } from './webapp.service.class';
import { Nullable } from 'classes/nullable';

@Injectable({
    providedIn: 'root',
})
export class WebApplicationService {
    private _subrelease = new Subscription();
    set subrelease(sub: TeardownLogic) {
        this._subrelease.add(sub);
    }

    private _SystemConfig = new BehaviorSubject<SystemConfigInfo | null>(null);
    get SystemConfig() {
        return this._SystemConfig.asObservable();
    }

    @SessionStorage() _Countries!: Country[];
    rootPath = '';

    constructor(private http: HttpClient, private configService: ConfigService) {
        this.rootPath = this.configService.hostAddress;

        // Force an initial load of the system config right at construction.
        if (!this._SystemConfig.value) {
            this.GetSystemConfig().subscribe();
        }
    }

    // #region Get Advertised Jobs

    GetAdvertisedJobs(jobType?: Nullable<string>, vacancyID?: Nullable<number>) {
        return this.http
            .get<AdvertisedJobInfo[]>(
                this.configService.hostAddress + `api/Vacancy/jobs?jobType=${jobType || ''}&vacancyID=${vacancyID || ''}`
            )
            .pipe(
                map((data) => {
                    data?.forEach((d) => {
                        d.VacancyEndDate = d.VacancyEndDate ? new Date(d.VacancyEndDate) : null;
                        d.Schedule = `${d.PositionMonths} Months, ${d.PositionFullPartTime}`;
                    });

                    return data;
                })
            );
    }

    // #endregion

    // #region Get Code Table Values

    GetCodeTableValues(codeValueID: Nullable<number>) {
        return this.http.get<CodeTableValue[]>(this.configService.hostAddress + `api/General/codes/${codeValueID || ''}`);
    }

    // #endregion

    // #region Get Countries

    GetCountries() {
        return this._Countries?.length
            ? of<Country[]>(this._Countries)
            : this.http.get<Country[]>(this.configService.hostAddress + `api/General/countries`).pipe(
                  tap((data) => {
                      this._Countries = data || [];
                  })
              );
    }

    // #endregion

    // #region Get Job Description

    GetJobDescription(localjobcode: Nullable<string>) {
        return this.http
            .get<JobDescriptionInfo>(this.configService.hostAddress + `api/Vacancy/description/${localjobcode || ''}`)
            .pipe(
                map((data) => {
                    if (data) {
                        data.EndDate = data.EndDate ? new Date(data.EndDate) : null;
                        data.BoardApproved = data.BoardApproved ? new Date(data.BoardApproved) : null;
                        data.Revised = data.Revised ? new Date(data.Revised) : null;
                        data.LastUpdated = data.LastUpdated ? new Date(data.LastUpdated) : null;
                    }

                    return data;
                })
            );
    }

    // #endregion

    // #region Get System Config

    GetSystemConfig(): Observable<SystemConfigInfo> {
        return this.http.get<SystemConfigInfo>(this.configService.hostAddress + `api/General/config`).pipe(
            map((data) => {
                data?.SystemWindows?.forEach((d) => {
                    d.BeginDate = d.BeginDate ? new Date(d.BeginDate) : null;
                    d.EndDate = d.EndDate ? new Date(d.EndDate) : null;
                });

                // Update in the storage.
                this._SystemConfig.next(data);

                return data;
            })
        );
    }

    // #endregion

    // #region Is Window Open

    IsWindowOpen(key: string) {
        if (!key || !this._SystemConfig.value) return false;

        const wins = this._SystemConfig.value.SystemWindows || [];

        const win = wins.find((w) => w.Description === key);

        return win?.Active === true;
    }

    // #endregion
}
